import React, { useEffect, useRef, useState } from 'react';
import {
  validateProcessPending,
  type ValidateProcessRequest,
  type ValidationResultDto,
} from '@api/processes';
import {
  EMPTY_VALIDATION,
  fromBackendValidationDto,
  type ValidationError,
  type ValidationResult,
} from './validation/types';

/** Tight debounce — feels instant without hammering the backend. */
const VALIDATE_DEBOUNCE_MS = 200;

interface UseGraphValidationArgs {
  /** Route param — undefined while the route is still resolving. */
  readonly processId: string | undefined;
  /** The wire payload the auto-save would also send. */
  readonly writeGraph: ValidateProcessRequest;
  /**
   * Validation result the backend included with the latest detail/save
   * response. Seeds the hook on first mount so first paint reflects the
   * server's truth instantly; subsequent refreshes flow through the
   * debounced POST below.
   */
  readonly seedFromDetail?: ValidationResultDto | null;
  /** Skip validation entirely (e.g. while a test run freezes the canvas). */
  readonly enabled?: boolean;
}

interface UseGraphValidationResult {
  readonly validation: ValidationResult;
  /** True between graph mutation and the next backend response. */
  readonly isValidating: boolean;
}

/**
 * Debounced backend-only validation for the editor canvas. The hook
 * shows the last-good {@link ValidationResult} while a request is in
 * flight, so structural edits never flash a misleading "all clear"
 * state mid-flight. Cancels in-flight requests when the graph changes
 * again so we never apply a stale response on top of a newer edit.
 */
export function useGraphValidation({
  processId,
  writeGraph,
  seedFromDetail,
  enabled = true,
}: UseGraphValidationArgs): UseGraphValidationResult {
  const [validation, setValidation] = useState<ValidationResult>(() =>
    seedFromDetail ? fromBackendValidationDto(seedFromDetail) : EMPTY_VALIDATION,
  );
  const [isValidating, setIsValidating] = useState<boolean>(false);

  const inflightRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!enabled || !processId) return;
    const pid = processId;
    const handle = globalThis.setTimeout(() => {
      inflightRef.current?.abort();
      const controller = new AbortController();
      inflightRef.current = controller;
      setIsValidating(true);
      validateProcessPending(pid, writeGraph, controller.signal)
        .then((result) => applyValidationResult(controller, result, setValidation, setIsValidating))
        .catch((err: unknown) => handleValidationError(controller, err, setIsValidating));
    }, VALIDATE_DEBOUNCE_MS);
    return () => {
      globalThis.clearTimeout(handle);
    };
  }, [processId, writeGraph, enabled]);

  useEffect(() => {
    return () => {
      inflightRef.current?.abort();
    };
  }, []);

  return { validation, isValidating };
}

/**
 * Fold a successful backend validation into the hook state. Pulled
 * out of the {@link useGraphValidation} effect to keep the inline
 * callback chain shallow (Sonar caps function nesting at 4).
 */
function applyValidationResult(
  controller: AbortController,
  result: ValidationResultDto,
  setValidation: React.Dispatch<React.SetStateAction<ValidationResult>>,
  setIsValidating: React.Dispatch<React.SetStateAction<boolean>>,
): void {
  if (controller.signal.aborted) return;
  // Skip the setState when the new result is structurally identical
  // to the current one — the editor context's dependency array
  // includes `errorsByNode` (so consumers re-render on validation
  // changes), so blindly handing out a fresh array reference every
  // 250ms turns every drag-pause into a full ProcessNode re-render
  // cascade.
  const next = fromBackendValidationDto(result);
  setValidation((prev) => (validationsEqual(prev, next) ? prev : next));
  setIsValidating(false);
}

/**
 * Mirror of {@link applyValidationResult} for the rejected path.
 * Keeps last-good validation on transient failures; the next save
 * round-trip will refresh validation from the detail anyway.
 */
function handleValidationError(
  controller: AbortController,
  err: unknown,
  setIsValidating: React.Dispatch<React.SetStateAction<boolean>>,
): void {
  if (controller.signal.aborted) return;
  if (isAbortError(err)) return;
  setIsValidating(false);
}

function isAbortError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const name = (err as { name?: unknown }).name;
  const code = (err as { code?: unknown }).code;
  return name === 'CanceledError' || name === 'AbortError' || code === 'ERR_CANCELED';
}

/**
 * Structural equality of two {@link ValidationResult}s. Backend rule
 * output is deterministic, so order-sensitive comparison is safe and
 * avoids the cost of building a {@code Set} on every response.
 */
function validationsEqual(a: ValidationResult, b: ValidationResult): boolean {
  if (a === b) return true;
  if (a.valid !== b.valid) return false;
  return errorsEqual(a.errors, b.errors)
    && errorsEqual(a.warnings, b.warnings)
    && errorsEqual(a.publishBlockers, b.publishBlockers);
}

function errorsEqual(
  a: readonly ValidationError[],
  b: readonly ValidationError[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (
      x.kind !== y.kind
      || x.nodeId !== y.nodeId
      || (x.edgeId ?? null) !== (y.edgeId ?? null)
      || (x.portId ?? null) !== (y.portId ?? null)
      || x.message !== y.message
    ) {
      return false;
    }
  }
  return true;
}
